import {objectFromEntries} from '@benchling/js-utils';
import assert from 'assert';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {assertAndContinue} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {PIPELINE_STEP_SHAPES} from './GeneratedPipelineStepShapes';
import {PipelineStepRawData, PipelineStep, GetPipelineStepShape, PipelineStepData} from './PipelineTypes';
import {getTypenameForStepDataKind} from './RawStepDataKindRegistry';
import {StepDataShape} from './StepShapeUtils';

/*
 ********************************
 *        PUBLIC UTILS
 ********************************
 */

/**
 * Narrow if a pipeline step data's raw data is a chart, data frame or file.
 */
export function pipelineStepRawDataIsOfKind<Kind extends PipelineStepDataKind>(
  kind: Kind,
  rawData: PipelineStepRawData
): rawData is PipelineStepRawData<Kind> {
  return rawData.__typename === getTypenameForStepDataKind(kind);
}

/**
 * Filter a pipeline step data's raw datas to those of an expected kind (chart, data frame, or file).
 */
export function getPipelineStepRawDatasOfKind<Kind extends PipelineStepDataKind>(
  kind: Kind,
  {rawDatas}: Pick<PipelineStepData, 'rawDatas'>
): Array<PipelineStepRawData<Kind>> {
  return rawDatas.filter((rawData) => pipelineStepRawDataIsOfKind(kind, rawData));
}

/**
 * Get the kind of an unknown raw data.
 *
 * If you expect a raw data to be of a specific kind, use `pipelineStepRawDataIsOfKind` or
 * `getPipelineStepRawDatasOfKind`, for type safety.
 */
export function getPipelineStepRawDataKind(rawData: PipelineStepRawData): PipelineStepDataKind {
  const kind = Object.values(PipelineStepDataKind).find((kind) => pipelineStepRawDataIsOfKind(kind, rawData));
  assert(kind != null, 'Unexpected raw data kind');
  return kind;
}

/**
 * Get raw datas for an input step data, by the step data's name.
 *
 * Example with type safety:
 *
 *    if (pipelineStepIsStepType(PipelineStepType.JOIN, 0, step)) {
 *        const {columns} = getInputStepDataRawDatas(step, 'Left')[0];
 *    }
 *
 */
export function getInputStepDataRawDatas<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataName extends _StepDataName<StepType, StepVersion, _StepDataRole.INPUT> = _StepDataName<
    StepType,
    StepVersion,
    _StepDataRole.INPUT
  >,
  Kind extends PipelineStepDataKind = _StepDataShapeKind<
    StepType,
    StepVersion,
    _StepDataRole.INPUT,
    DataName
  >,
>(step: PipelineStep<StepType, StepVersion>, stepDataName: DataName): Array<PipelineStepRawData<Kind>> {
  return getStepDataRawDatas(step, stepDataName, _StepDataRole.INPUT);
}

/**
 * Get raw datas for an output step data, by the step data's name.
 *
 * Example with type safety:
 *
 *   if (pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step)) {
 *       const {rasterDownloadUrl} = getOutputStepDataRawDatas(step, 'Output chart')[0];
 *       const {columns} = getOutputStepDataRawDatas(step, 'Output dataset')[0];
 *    }
 */
export function getOutputStepDataRawDatas<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataName extends _StepDataName<StepType, StepVersion, _StepDataRole.OUTPUT> = _StepDataName<
    StepType,
    StepVersion,
    _StepDataRole.OUTPUT
  >,
  Kind extends PipelineStepDataKind = _StepDataShapeKind<
    StepType,
    StepVersion,
    _StepDataRole.OUTPUT,
    DataName
  >,
>(step: PipelineStep<StepType, StepVersion>, stepDataName: DataName): Array<PipelineStepRawData<Kind>> {
  return getStepDataRawDatas(step, stepDataName, _StepDataRole.OUTPUT);
}

/**
 * Get all output raw datas of a step, optionally of a particular type.
 */
export function getAllOutputStepDataRawDatas<Kind extends PipelineStepDataKind>(
  step: PipelineStep,
  kind?: Kind
): Array<PipelineStepRawData<Kind>> {
  return Object.values(getStepDatasRecord(step, _StepDataRole.OUTPUT, kind)).flatMap(
    (rawDatas) => rawDatas
  ) as Array<PipelineStepRawData<Kind>>;
}

/**
 * Get the input raw data for a single-input step.
 *
 * This will assert and fail if the are multiple inputs, or if the input step data is empty.
 *
 * Example with type safety:
 *
 *    assertPipelineStepIsStepType(PipelineStepType.SORT_COLUMN, 0, step);
 *    const {columns} = getSingleInputRawData(step);
 */
export function getSingleInputRawData<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Kind extends PipelineStepDataKind = _StepDataShapeKind<StepType, StepVersion, _StepDataRole.INPUT>,
>(step: PipelineStep<StepType, StepVersion>): PipelineStepRawData<Kind> {
  return getSingleRawData(step, _StepDataRole.INPUT);
}

/**
 * Get the input raw data for a single-input step, or null if the step has not yet produced input data.
 *
 * This will assert and fail if there are multiple inputs.
 *
 * Example with type safety:
 *
 *    assertPipelineStepIsStepType(PipelineStepType.SORT_COLUMN, 0, step);
 *    const inputRawData = tryGetSingleInputRawData(step);
 */
export function tryGetSingleInputRawData<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Kind extends PipelineStepDataKind = _StepDataShapeKind<StepType, StepVersion, _StepDataRole.INPUT>,
>(step: PipelineStep<StepType, StepVersion>): PipelineStepRawData<Kind> | null {
  const stepDataNames = getStepDataNames(step.stepType, step.stepVersion, _StepDataRole.INPUT);
  assert(stepDataNames.length === 1, `${step.stepType} v${step.stepVersion} has multiple step datas`);

  const rawDatas = getStepDataRawDatas(step, stepDataNames[0], _StepDataRole.INPUT);

  if (rawDatas.length === 0) {
    return null;
  }
  assert(rawDatas.length === 1, `${step.stepType} has ${rawDatas.length} raw input datas`);

  return rawDatas[0] as PipelineStepRawData<Kind>;
}

/**
 * Get the output raw data for a single-output step.
 *
 * This will assert and fail if the are multiple outputs, or if the output step data is empty.
 *
 * Example with type safety:
 *
 *    assertPipelineStepIsStepType(PipelineStepType.SORT_COLUMN, 0, step);
 *    const {columns} = getSingleInputRawData(step);
 */
export function getSingleOutputRawData<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Kind extends PipelineStepDataKind = _StepDataShapeKind<StepType, StepVersion, _StepDataRole.OUTPUT>,
>(step: PipelineStep<StepType, StepVersion>): PipelineStepRawData<Kind> {
  return getSingleRawData(step, _StepDataRole.OUTPUT);
}

/**
 * Get all input step datas' raw datas for a step
 */
export function getInputStepDatasRecord<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Kind extends PipelineStepDataKind = PipelineStepDataKind,
>(step: PipelineStep<StepType, StepVersion>, kind?: Kind): InputStepDatasRecord<StepType, StepVersion, Kind> {
  return getStepDatasRecord(step, _StepDataRole.INPUT, kind);
}

/**
 * Get all output step datas' raw datas for a step
 */
export function getOutputStepDatasRecord<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Kind extends PipelineStepDataKind = PipelineStepDataKind,
>(
  step: PipelineStep<StepType, StepVersion>,
  kind?: Kind
): OutputStepDatasRecord<StepType, StepVersion, Kind> {
  return getStepDatasRecord(step, _StepDataRole.OUTPUT, kind);
}

/**
 * Get the expected step data kind, for a given step and step data.
 */
export function getExpectedStepDataKind<StepType extends PipelineStepType, StepVersion extends number>(
  step: PipelineStep<StepType, StepVersion>,
  stepData: Pick<PipelineStepData, 'id' | 'name'>
): PipelineStepDataKind {
  // Check by id rather than by name, to not assume input and output step datas have disjoint names
  const stepDataRole = step.inputStepDatas.some(({id}) => id === stepData.id)
    ? _StepDataRole.INPUT
    : _StepDataRole.OUTPUT;

  if (!isValidStepDataName(step.stepType, step.stepVersion, stepDataRole, stepData.name)) {
    assertAndContinue(
      false,
      `Unexpected step data name '${stepData.name}' for ${step.stepType} v${step.stepVersion}`
    );
    return PipelineStepDataKind.DATASET;
  }

  return getStepDataKind(step.stepType, step.stepVersion, stepDataRole, stepData.name);
}

/**
 * Check if input step datas are complete; i.e. every step data has a raw data or is not required
 */
export function inputStepDatasAreComplete<StepType extends PipelineStepType, StepVersion extends number>(
  step: PipelineStep<StepType, StepVersion>
): boolean {
  return stepDatasAreComplete(step, _StepDataRole.INPUT);
}

/**
 * Check if output step datas are complete; i.e. every step data has a raw data or is not required
 */
export function outputStepDatasAreComplete<StepType extends PipelineStepType, StepVersion extends number>(
  step: PipelineStep<StepType, StepVersion>
): boolean {
  return stepDatasAreComplete(step, _StepDataRole.OUTPUT);
}

/**
 * Check if the draft state of datas is complete, according to the shape constrained by stepType and stepVersion.
 */
export function draftInputDataFramesIsComplete<StepType extends PipelineStepType, StepVersion extends number>(
  stepType: StepType,
  stepVersion: StepVersion,
  inputDataFrames: InputDataFrames<PipelineStepType, StepVersion>
): boolean {
  return getStepDataNames(stepType, stepVersion, _StepDataRole.INPUT).every(
    (stepDataName) =>
      (inputDataFrames[stepDataName] ?? []).length > 0 ||
      !getStepDataShape(stepType, stepVersion, _StepDataRole.INPUT, stepDataName).isRequired
  );
}

/**
 * Get the name of the single dataframe output step data for a given step type and step version.
 */
export function getSingleDataFrameOutputStepDataName({
  stepType,
  stepVersion,
}: Pick<PipelineStep, 'stepType' | 'stepVersion'>): string {
  const stepDataNames = getStepDataNamesOfKind(
    stepType,
    stepVersion,
    _StepDataRole.OUTPUT,
    PipelineStepDataKind.DATASET
  );
  assert(stepDataNames.length === 1, `Expected 1 output dataset step data for ${stepType} v${stepVersion}`);
  return stepDataNames[0];
}

/**
 * Get the name of the single dataframe input step data for a given step type and step version.
 */
export function getSingleDataFrameInputStepDataName({
  stepType,
  stepVersion,
}: Pick<PipelineStep, 'stepType' | 'stepVersion'>): string {
  let stepDataNames = getStepDataNamesOfKind(
    stepType,
    stepVersion,
    _StepDataRole.INPUT,
    PipelineStepDataKind.DATASET
  );
  // If there are multiple step datas, use the generically named one, instead of erroring. See REGERSSION_INTERPOLATION.
  if (stepDataNames.length > 1) {
    stepDataNames = stepDataNames.filter(
      (stepDataName) => stepDataName === SINGLE_DATASET_INPUT_STEP_DATA_NAME
    );
  }
  assert(stepDataNames.length === 1, `Expected 1 input dataset step data for ${stepType} v${stepVersion}`);
  return stepDataNames[0];
}

/** Get the step data shape for an output step data. */
export function getOutputStepDataShape(
  stepType: PipelineStepType,
  stepVersion: number,
  stepDataName: string
): StepDataShape {
  assert(
    isValidStepDataName(stepType, stepVersion, _StepDataRole.OUTPUT, stepDataName),
    `Invalid output step data name '${stepDataName}' for ${stepType} v${stepVersion}`
  );
  return getStepDataShape(stepType, stepVersion, _StepDataRole.OUTPUT, stepDataName);
}

/** Get the step data shape for an input step data. */
export function getInputStepDataShape(
  stepType: PipelineStepType,
  stepVersion: number,
  stepDataName: string
): StepDataShape {
  assert(isValidStepDataName(stepType, stepVersion, _StepDataRole.INPUT, stepDataName));
  return getStepDataShape(stepType, stepVersion, _StepDataRole.INPUT, stepDataName);
}

/*
 *********************************
 *         PUBLIC TYPES
 *********************************
 */

/**
 * A map of step data names to input raw datas.
 */
export type InputStepDatasRecord<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Kind extends PipelineStepDataKind = PipelineStepDataKind,
> = _StepDatasRecord<StepType, StepVersion, _StepDataRole.INPUT, Kind>;

/**
 * A map of step data names to output raw datas.
 */
export type OutputStepDatasRecord<
  StepType extends PipelineStepType,
  StepVersion extends number,
  Kind extends PipelineStepDataKind = PipelineStepDataKind,
> = _StepDatasRecord<StepType, StepVersion, _StepDataRole.OUTPUT, Kind>;

/**
 * Create a mapped type of step data names to a type narrowed to a specific step type and version.
 *
 * @package
 */
export type InputStepDataFramesMappedType<
  TStepType extends PipelineStepType,
  TStepVersion extends number,
  TMapToType,
> = Record<keyof InputStepDatasRecord<TStepType, TStepVersion, PipelineStepDataKind.DATASET>, TMapToType>;

/**
 * Narrow `InputStepDatasRecord` to only include minimal information about the data frames.
 */
export type InputDataFrames<
  StepType extends PipelineStepType,
  StepVersion extends number,
> = InputStepDataFramesMappedType<StepType, StepVersion, Array<DataFrameSpec>>;

enum _StepDataRole {
  INPUT = 'inputDataShapes',
  OUTPUT = 'outputDataShapes',
}

type _StepDataName<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataRole extends _StepDataRole = _StepDataRole,
> = keyof GetPipelineStepShape<StepType, StepVersion>[DataRole] extends string
  ? keyof GetPipelineStepShape<StepType, StepVersion>[DataRole]
  : never;

type _StepDataShape<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataRole extends _StepDataRole = _StepDataRole,
  DataName extends _StepDataName<StepType, StepVersion, DataRole> = _StepDataName<
    StepType,
    StepVersion,
    DataRole
  >,
> = GetPipelineStepShape<StepType, StepVersion>[DataRole][DataName] extends StepDataShape
  ? GetPipelineStepShape<StepType, StepVersion>[DataRole][DataName]
  : never;

type _StepDataShapeKind<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataRole extends _StepDataRole = _StepDataRole,
  DataName extends _StepDataName<StepType, StepVersion, DataRole> = _StepDataName<
    StepType,
    StepVersion,
    DataRole
  >,
> = _StepDataShape<StepType, StepVersion, DataRole, DataName>['kind'];

type _StepDatasRecord<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataRole extends _StepDataRole,
  Kind extends PipelineStepDataKind = PipelineStepDataKind,
> = {
  [DataName in _StepDataName<StepType, StepVersion, DataRole> as Extract<
    _StepDataShapeKind<StepType, StepVersion, DataRole, DataName>,
    Kind
  > extends never
    ? never
    : DataName]: Array<PipelineStepRawData<_StepDataShapeKind<StepType, StepVersion, DataRole, DataName>>>;
};

/*
 ********************************
 *        HELPER UTILS
 ********************************
 */

/**
 * Get the step data shape for a given step type, step version, and step data name.
 */
function getStepDataShape<
  StepType extends PipelineStepType,
  StepVersion extends number,
  StepDataRole extends _StepDataRole,
  StepDataName extends _StepDataName<StepType, StepVersion, StepDataRole>,
>(
  stepType: StepType,
  stepVersion: StepVersion,
  stepDataRole: StepDataRole,
  stepDataName: StepDataName
): _StepDataShape<StepType, StepVersion, StepDataRole, StepDataName> {
  return PIPELINE_STEP_SHAPES[stepType][stepVersion][stepDataRole][stepDataName] as _StepDataShape<
    StepType,
    StepVersion,
    StepDataRole,
    StepDataName
  >;
}

/**
 * Get the step data kind for a given step type, step version, and step data name.
 */
function getStepDataKind<
  StepType extends PipelineStepType,
  StepVersion extends number,
  StepDataRole extends _StepDataRole,
  StepDataName extends _StepDataName<StepType, StepVersion, StepDataRole>,
>(
  stepType: StepType,
  stepVersion: StepVersion,
  stepDataRole: StepDataRole,
  stepDataName: StepDataName
): _StepDataShapeKind<StepType, StepVersion, StepDataRole, StepDataName> {
  return getStepDataShape(stepType, stepVersion, stepDataRole, stepDataName).kind;
}

/**
 * Get the step data names for a given step type, step version, and step data name.
 */
function getStepDataNames<
  StepType extends PipelineStepType,
  StepVersion extends number,
  StepDataRole extends _StepDataRole,
>(
  stepType: StepType,
  stepVersion: StepVersion,
  stepDataRole: StepDataRole
): Array<_StepDataName<StepType, StepVersion, StepDataRole>> {
  return Object.keys(PIPELINE_STEP_SHAPES[stepType][stepVersion][stepDataRole]) as Array<
    _StepDataName<StepType, StepVersion, StepDataRole>
  >;
}

/**
 * Check if a step data name is valid for a step type and step version.
 */
function isValidStepDataName<
  StepType extends PipelineStepType,
  StepVersion extends number,
  StepDataRole extends _StepDataRole,
>(
  stepType: StepType,
  stepVersion: StepVersion,
  stepDataRole: StepDataRole,
  stepDataName: string
): stepDataName is _StepDataName<StepType, StepVersion, StepDataRole> {
  return getStepDataNames(stepType, stepVersion, stepDataRole).some((name) => name === stepDataName);
}

/**
 * Shared helper for `get[Input/Output]StepDataRawDatas`
 */
function getStepDataRawDatas<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataRole extends _StepDataRole,
  DataName extends _StepDataName<StepType, StepVersion, DataRole> = _StepDataName<
    StepType,
    StepVersion,
    DataRole
  >,
  Kind extends PipelineStepDataKind = _StepDataShapeKind<StepType, StepVersion, DataRole, DataName>,
>(
  step: PipelineStep<StepType, StepVersion>,
  stepDataName: DataName,
  stepDataRole: DataRole
): Array<PipelineStepRawData<Kind>> {
  assert(
    isValidStepDataName(step.stepType, step.stepVersion, stepDataRole, stepDataName),
    `${stepDataName} is not a step data for ${step.stepType} v${step.stepVersion}`
  );

  const kind = getStepDataKind(step.stepType, step.stepVersion, stepDataRole, stepDataName);

  const stepDatas = stepDataRole === _StepDataRole.INPUT ? step.inputStepDatas : step.outputStepDatas;
  const stepData = stepDatas.find(({name}) => name === stepDataName);

  if (stepData == null) {
    assertAndContinue(false, `Step data ${stepDataName} missing for ${step.stepType} v${step.stepVersion}`);
    return [];
  }

  return getPipelineStepRawDatasOfKind(kind, stepData);
}

/**
 * Shared helper for `getSingle[Input/Output]RawData
 */
export function getSingleRawData<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataRole extends _StepDataRole,
  Kind extends PipelineStepDataKind = _StepDataShapeKind<StepType, StepVersion, DataRole>,
>(step: PipelineStep<StepType, StepVersion>, stepDataRole: DataRole): PipelineStepRawData<Kind> {
  const stepDataNames = getStepDataNames(step.stepType, step.stepVersion, stepDataRole);
  assert(stepDataNames.length === 1, `${step.stepType} v${step.stepVersion} has multiple step datas`);

  const rawDatas = getStepDataRawDatas(step, stepDataNames[0], stepDataRole);

  assert(rawDatas.length > 0, `${step.stepType} has no raw output datas`);
  assertAndContinue(rawDatas.length === 1, `${step.stepType} has ${rawDatas.length} raw output datas`);

  return rawDatas[0];
}

/**
 * Shared helper for `get[Input/Output]StepDatasRecord`
 */
function getStepDatasRecord<
  StepType extends PipelineStepType,
  StepVersion extends number,
  DataRole extends _StepDataRole,
  Kind extends PipelineStepDataKind = PipelineStepDataKind,
>(
  step: PipelineStep<StepType, StepVersion>,
  stepDataRole: DataRole,
  kind?: Kind
): _StepDatasRecord<StepType, StepVersion, DataRole, Kind> {
  const stepDataNames = getStepDataNames(step.stepType, step.stepVersion, stepDataRole).filter(
    (stepDataName) =>
      kind == null || kind === getStepDataKind(step.stepType, step.stepVersion, stepDataRole, stepDataName)
  );

  return objectFromEntries(
    stepDataNames.map((stepDataName) => [stepDataName, getStepDataRawDatas(step, stepDataName, stepDataRole)])
  ) as _StepDatasRecord<StepType, StepVersion, DataRole, Kind>;
}

/**
 * Shared helper for `[input/output]StepDatasAreComplete
 */
function stepDatasAreComplete<StepType extends PipelineStepType, StepVersion extends number>(
  step: PipelineStep<StepType, StepVersion>,
  stepDataRole: _StepDataRole
): boolean {
  return getStepDataNames(step.stepType, step.stepVersion, stepDataRole).every(
    (stepDataName) =>
      getStepDataRawDatas(step, stepDataName, stepDataRole).length > 0 ||
      !getStepDataShape(step.stepType, step.stepVersion, _StepDataRole.INPUT, stepDataName).isRequired
  );
}

/**
 * Get the step data names for a given step type, step version, step data name, and step data kind.
 */
function getStepDataNamesOfKind<
  StepType extends PipelineStepType,
  StepVersion extends number,
  StepDataRole extends _StepDataRole,
  Kind extends PipelineStepDataKind,
>(
  stepType: StepType,
  stepVersion: StepVersion,
  stepDataRole: StepDataRole,
  kind: PipelineStepDataKind
): Array<keyof _StepDatasRecord<StepType, StepVersion, StepDataRole, Kind>> {
  return getStepDataNames(stepType, stepVersion, stepDataRole).filter(
    (stepDataName) => getStepDataKind(stepType, stepVersion, stepDataRole, stepDataName) === kind
  );
}
